import { useState } from 'react';
import clsx from 'clsx';
import { AmountUtils, CurrencyUtils, FormatUtils, PaletteUtils } from '@shared/utils';
import type { DashboardSummariesResponse } from '@shared/api/rest/dashboards';
import { ColorsEnum, Icon, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';

interface IPaymentProps {
  mainAccount: DashboardSummariesResponse;
  subAccounts: DashboardSummariesResponse[];
}

export const PaymentItems = (props: IPaymentProps) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  return (
    <div className={clsx('min-h-32 flex bg-white dark:bg-soft-black mx-0 my-6', props.subAccounts && 'ml-8')}>
      <div
        className={clsx(
          'min-w-16 w-16 bg-gradient-to-b',
          PaletteUtils.isVcfcPalette && 'from-primary-light to-primary-dark',
          PaletteUtils.isInsigniaPalette && 'from-black to-yellow-ocher',
        )}
      />
      <div className='w-full py-3.5 px-3'>
        <div className='gap-3'>
          <div className='flex justify-between'>
            <Text size='3xl'>{AmountUtils.transformToAmountWithCurrency(props.mainAccount.availableBalance, props.mainAccount.currency)}</Text>
            <div className='flex gap-1 h-full'>
              <div className='flex items-center font-semibold text-base px-2.5 border text-light-gray rounded h-8'>
                {CurrencyUtils.getSymbolByCode(props.mainAccount.currency)}
              </div>
              {props.subAccounts?.length > 0 &&
                props.subAccounts.map((account) => {
                  return (
                    <div key={account.id} className='flex items-center font-semibold text-base px-2.5 border text-light-gray rounded h-8'>
                      {CurrencyUtils.getSymbolByCode(account.currency)}
                    </div>
                  );
                })}
              <div className='flex items-center mx-2 cursor-pointer' onClick={() => setIsCollapsed((prev) => !prev)}>
                <div className={clsx('-rotate-90 transition-transform', isCollapsed && 'rotate-90')}>
                  <Icon icon={<ChevronIcon />} />
                </div>
              </div>
            </div>
          </div>
          <div className='flex gap-3'>
            <div className='mt-3 border-r border-light pr-3'>
              <Text weight='semibold'>{props.mainAccount.accountName || FormatUtils.formatAccountNumber(props.mainAccount.accountNumber)}</Text>
              <Text weight='semibold' size='xs' color={ColorsEnum.Gray} isUppercase>
                {props.mainAccount.productName}
              </Text>
            </div>
            <div className='mt-3'>
              <h3 className='text-soft-black dark:text-white font-semibold text-base'>{props.mainAccount.iban}</h3>
              <h4 className='uppercase text-pearl-dark-gray font-semibold text-[10px]'>iban</h4>
            </div>
          </div>
        </div>
        <hr className='mx-0 my-3 text-light' />
        {!isCollapsed && (
          <div className='flex flex-col gap-3'>
            <div key={props.mainAccount.id} className='flex gap-6 max-[1024px]:flex-col max-[1024px]:gap-2 bg-ice dark:bg-slate py-2 px-3'>
              <div
                className={clsx(
                  'border justify-center uppercase text-primary rounded h-full flex items-center px-2 p-0.5 font-semibold text-center',
                  PaletteUtils.isVcfcPalette && 'dark:text-white',
                )}
              >
                <span className='text-base mr-2 ml-1'>{CurrencyUtils.getSymbolByCode(props.mainAccount.currency)}</span>
                <span className='text-xs uppercase mt-0.5'>{props.mainAccount.mainAccId ? 'Sub account' : 'Main Account'}</span>
              </div>
              <div>
                <h3 className='text-slate dark:text-gray-dark text-xs font-normal'>Account balance</h3>
                <h4 className='text-left text-soft-black dark:text-white text-xs font-semibold'>
                  {AmountUtils.transformToAmountWithCurrency(props.mainAccount.availableBalance, props.mainAccount.currency)}
                </h4>
              </div>
              <div>
                <h3 className='text-slate dark:text-gray-dark text-xs font-normal'>Outgoing Amount</h3>
                <h4 className='text-left text-soft-black dark:text-white text-xs font-semibold'>
                  {AmountUtils.transformToAmountWithCurrency(props.mainAccount.outgoingAmount, props.mainAccount.currency)}
                </h4>
              </div>
              <div>
                <h3 className='text-slate dark:text-gray-dark text-xs font-normal'>Incoming Amount</h3>
                <h4 className='text-left text-soft-black dark:text-white text-xs font-semibold'>
                  {AmountUtils.transformToAmountWithCurrency(props.mainAccount.incomingAmount, props.mainAccount.currency)}
                </h4>
              </div>
              <div>
                <h3 className='text-slate dark:text-gray-dark text-xs font-normal'>Pending transactions summary</h3>
                <h4 className='text-left text-soft-black dark:text-white text-xs font-semibold'>
                  {AmountUtils.transformToAmountWithCurrency(props.mainAccount.pendingTransactionsSummary, props.mainAccount.currency)}
                </h4>
              </div>
            </div>
            {props.subAccounts.map((account) => {
              return (
                <div key={account.id} className='flex gap-6 max-[1024px]:flex-col max-[1024px]:gap-2 bg-ice dark:bg-slate py-2 px-3'>
                  <div
                    className={clsx(
                      'border justify-center uppercase text-primary rounded h-full flex items-center px-2 p-0.5 font-semibold text-center',
                      PaletteUtils.isVcfcPalette && ' dark:text-white',
                    )}
                  >
                    <span className='text-base mr-2 ml-1'>{CurrencyUtils.getSymbolByCode(account.currency)}</span>
                    <span className='text-xs uppercase mt-0.5'>Sub account</span>
                  </div>
                  <div>
                    <h3 className='text-slate dark:text-gray-dark text-xs font-normal'>Account balance</h3>
                    <h4 className='text-left text-soft-black dark:text-white text-xs font-semibold'>
                      {AmountUtils.transformToAmountWithCurrency(account.availableBalance, account.currency)}
                    </h4>
                  </div>
                  <div>
                    <h3 className='text-slate dark:text-gray-dark text-xs font-normal'>Outgoing Amount</h3>
                    <h4 className='text-left text-soft-black dark:text-white text-xs font-semibold'>
                      {AmountUtils.transformToAmountWithCurrency(account.outgoingAmount, account.currency)}
                    </h4>
                  </div>
                  <div>
                    <h3 className='text-slate dark:text-gray-dark text-xs font-normal'>Incoming Amount</h3>
                    <h4 className='text-left text-soft-black dark:text-white text-xs font-semibold'>
                      {AmountUtils.transformToAmountWithCurrency(account.incomingAmount, account.currency)}
                    </h4>
                  </div>
                  <div>
                    <h3 className='text-slate dark:text-gray-dark text-xs font-normal'>Pending transactions summary</h3>
                    <h4 className='text-left text-soft-black dark:text-white text-xs font-semibold'>
                      {AmountUtils.transformToAmountWithCurrency(account.pendingTransactionsSummary, account.currency)}
                    </h4>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
